/**
 * Admin → Drive OAuth setup page.
 *
 * Replaces the old manual ``oauth_drive_setup.py`` step with a one-click
 * web flow:
 *   1. Show current token state (drive scope present / missing).
 *   2. "Connect Google Drive" button → calls /api/admin/drive-oauth/start
 *      and navigates the browser to Google's consent screen.
 *   3. Google redirects back to /api/admin/drive-oauth/callback, which
 *      writes token.json and bounces the browser back here with
 *      ``?drive_oauth=success`` (or ``?drive_oauth=error&reason=…``).
 */
import { useEffect, useState, useCallback } from "react";
import { ShieldCheck, Cloud, CheckCircle, XCircle, Spinner, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import AdminPageHeader from "@/components/AdminPageHeader";
import { useT } from "@/lib/i18n";

const PAGE_PATH = "/admin/drive-oauth";

export default function DriveOauthAdminPage({ user, onLogout, onLogoutAll }) {
  const t = useT();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  const load = useCallback(async () => {
    if (!user?.email) return;
    setLoading(true);
    try {
      const { data } = await api.get("/admin/drive-oauth/status", {
        params: { requester_email: user.email },
      });
      setStatus(data);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(`${t("drive_oauth.load_failed", "Could not load Drive OAuth status")}: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [user?.email, t]);

  useEffect(() => { load(); }, [load]);

  // Pick up the ?drive_oauth=success|error redirect from the callback.
  useEffect(() => {
    const url = new URL(window.location.href);
    const flag = url.searchParams.get("drive_oauth");
    if (flag === "success") {
      toast.success(t("drive_oauth.toast_success", "Google Drive connected"));
      url.searchParams.delete("drive_oauth");
      window.history.replaceState({}, "", url.pathname + (url.search ? `?${url.searchParams}` : ""));
      load();
    } else if (flag === "error") {
      const reason = url.searchParams.get("reason") || "unknown";
      toast.error(`${t("drive_oauth.toast_error", "Drive OAuth failed")}: ${decodeURIComponent(reason)}`);
      url.searchParams.delete("drive_oauth");
      url.searchParams.delete("reason");
      window.history.replaceState({}, "", url.pathname + (url.search ? `?${url.searchParams}` : ""));
    }
  }, [load, t]);

  const startConnect = async () => {
    if (!user?.email) return;
    setStarting(true);
    try {
      const { data } = await api.post("/admin/drive-oauth/start", {
        requester_email: user.email,
        return_to: PAGE_PATH,
      });
      // Bounce the browser to Google.
      window.location.href = data.auth_url;
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(`${t("drive_oauth.start_failed", "Could not start OAuth")}: ${msg}`);
      setStarting(false);
    }
  };

  const driveOk = !!status?.drive_scope_present;
  const credsOk = !!status?.credentials_present;
  const tokenExists = !!status?.token_exists;

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminPageHeader
        sticky
        maxWidth="max-w-[1100px]"
        testId="drive-oauth-back-home"
        icon={<ShieldCheck size={20} weight="fill" className="text-blue-700" />}
        title={t("drive_oauth.page_title", "Google Drive OAuth")}
        user={user}
        onLogout={onLogout}
        onLogoutAll={onLogoutAll}
      />

      <main className="max-w-[800px] mx-auto px-6 py-8">
        <h2 className="text-2xl font-black font-['Chivo'] mb-2">
          {t("drive_oauth.heading", "Connect Google Drive for admin uploads")}
        </h2>
        <p className="text-sm text-slate-500 mb-6">
          {t("drive_oauth.intro",
             "Public files uploaded by admins are mirrored to Google Drive in addition to GridFS. This page wires up the OAuth scope without needing shell access to the server.")}
        </p>

        {loading ? (
          <div className="text-sm text-slate-500 inline-flex items-center gap-2" data-testid="drive-oauth-loading">
            <Spinner size={14} className="animate-spin" />
            {t("drive_oauth.loading", "Checking current Drive OAuth status…")}
          </div>
        ) : (
          <div data-testid="drive-oauth-card" className="bg-white border border-slate-200 rounded-sm p-5 mb-5 space-y-3">
            <Row
              label={t("drive_oauth.row_creds", "credentials.json on server")}
              ok={credsOk}
              fixHint={t("drive_oauth.fix_creds",
                         "Place the OAuth client secret JSON at backend/credentials.json.")}
            />
            <Row
              label={t("drive_oauth.row_token", "token.json present")}
              ok={tokenExists}
              fixHint={t("drive_oauth.fix_token",
                         "No token yet — click \"Connect Google Drive\" below.")}
            />
            <Row
              label={t("drive_oauth.row_drive_scope", "drive.file scope granted")}
              ok={driveOk}
              fixHint={t("drive_oauth.fix_scope",
                         "Re-consent below to add the missing scope.")}
            />
            <div className="text-[11px] text-slate-500 pt-2 border-t border-slate-100">
              {t("drive_oauth.scopes_label", "Active scopes:")}{" "}
              <span className="font-mono text-slate-700">
                {(status?.scopes || []).join(", ") || t("drive_oauth.none", "(none)")}
              </span>
            </div>
            <div className="text-[11px] text-slate-500">
              {t("drive_oauth.redirect_label", "Redirect URI to register in Google Cloud Console:")}
              <br />
              <code className="font-mono text-[10px] text-slate-700 break-all">{status?.redirect_uri_to_register}</code>
            </div>
          </div>
        )}

        {!loading && !credsOk && (
          <div className="bg-amber-50 border-l-4 border-amber-500 rounded-sm p-4 mb-5 text-sm text-amber-900 flex items-start gap-2" data-testid="drive-oauth-warn-creds">
            <Warning size={18} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
            <div>
              <b>{t("drive_oauth.warn_creds_title", "credentials.json missing")}</b>
              <p className="mt-1">
                {t("drive_oauth.warn_creds_body",
                   "Add the OAuth client secret to the server before continuing. The OAuth client must be of type \"Web application\" and the redirect URI shown above must be authorized in Google Cloud Console.")}
              </p>
            </div>
          </div>
        )}

        <button
          data-testid="drive-oauth-connect-btn"
          disabled={starting || loading || !credsOk}
          onClick={startConnect}
          className="px-5 py-2 bg-blue-700 text-white text-xs font-bold uppercase rounded-sm hover:bg-blue-800 disabled:opacity-40 inline-flex items-center gap-2"
        >
          {starting
            ? <Spinner size={13} className="animate-spin" />
            : <Cloud size={14} weight="fill" />}
          {driveOk
            ? t("drive_oauth.reauthorize", "Re-authorize Google Drive")
            : t("drive_oauth.connect", "Connect Google Drive")}
        </button>
      </main>
    </div>
  );
}


function Row({ label, ok, fixHint }) {
  return (
    <div className="flex items-start gap-3 text-sm">
      {ok
        ? <CheckCircle size={16} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
        : <XCircle size={16} weight="fill" className="text-rose-600 shrink-0 mt-0.5" />}
      <div className="flex-1">
        <div className="font-bold">{label}</div>
        {!ok && <div className="text-[11px] text-slate-500 mt-0.5">{fixHint}</div>}
      </div>
    </div>
  );
}
